import { useQuery } from '@tanstack/react-query';
import { supabase } from '../utils/supabaseClient';

// Type definitions matching RPC output structure
export interface CategoryNode {
  name: string;
  count: number;
  category_ids: string[];
  tier2_subcategories?: CategoryNode[];
  tier3_details?: CategoryNode[];
}

export interface ReaderSegment {
  count: number;
  tier1_categories: CategoryNode[];
}

export interface AuthorAttributeCategory {
  category_id: string;
  accepts_self_published: boolean;
  is_debut_only: boolean;
  has_regional_restriction: boolean;
  supports_young_authors: boolean;
}

export interface AuthorAttributes {
  self_published_count: number;
  debut_only_count: number;
  regional_restriction_count: number;
  young_authors_count: number;
  categories_with_attributes: AuthorAttributeCategory[];
}

export interface CategoryExplorerTree {
  reader_segments: {
    'Books for Adults'?: ReaderSegment;
    'Books for Children & Young Adult'?: ReaderSegment;
  };
  author_attributes: AuthorAttributes;
  metadata: {
    total_categories: number;
    filtered_noise_count: number;
    generated_at: string;
  };
}

/**
 * Fetches the category explorer tree from Supabase RPC
 */
async function fetchCategoryExplorerTree(): Promise<CategoryExplorerTree> {
  const { data, error } = await supabase.rpc('get_category_explorer_tree');

  if (error) {
    console.error('Error fetching category explorer tree:', error);
    throw new Error(`Failed to fetch category tree: ${error.message}`);
  }

  if (!data) {
    throw new Error('No data returned from category explorer tree RPC');
  }

  return data as CategoryExplorerTree;
}

/**
 * React Query hook for fetching category explorer tree
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useCategoryExplorerTree();
 *
 * if (isLoading) return <div>Loading categories...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 *
 * const adultCategories = data?.reader_segments['Books for Adults']?.tier1_categories || [];
 * ```
 */
export function useCategoryExplorerTree() {
  return useQuery<CategoryExplorerTree, Error>({
    queryKey: ['category-explorer-tree'],
    queryFn: fetchCategoryExplorerTree,
    staleTime: 10 * 60 * 1000, // 10 minutes - category data doesn't change often
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
    refetchOnWindowFocus: false,
    retry: 2,
  });
}

/**
 * Helper function to extract tier1 categories for a specific reader segment
 */
export function getTier1Categories(
  data: CategoryExplorerTree | undefined,
  segment: 'Books for Adults' | 'Books for Children & Young Adult'
): CategoryNode[] {
  return data?.reader_segments[segment]?.tier1_categories || [];
}

/**
 * Helper function to extract tier2 subcategories for a specific tier1 category
 */
export function getTier2Subcategories(
  tier1Category: CategoryNode | undefined
): CategoryNode[] {
  return tier1Category?.tier2_subcategories || [];
}

/**
 * Helper function to extract tier3 details for a specific tier2 subcategory
 */
export function getTier3Details(
  tier2Subcategory: CategoryNode | undefined
): CategoryNode[] {
  return tier2Subcategory?.tier3_details || [];
}

/**
 * Helper function to get author attribute categories
 */
export function getAuthorAttributeCategories(
  data: CategoryExplorerTree | undefined,
  filter?: {
    selfPublished?: boolean;
    debutOnly?: boolean;
    regional?: boolean;
    youngAuthors?: boolean;
  }
): AuthorAttributeCategory[] {
  const categories = data?.author_attributes.categories_with_attributes || [];

  if (!filter) return categories;

  return categories.filter(cat => {
    if (filter.selfPublished && !cat.accepts_self_published) return false;
    if (filter.debutOnly && !cat.is_debut_only) return false;
    if (filter.regional && !cat.has_regional_restriction) return false;
    if (filter.youngAuthors && !cat.supports_young_authors) return false;
    return true;
  });
}
